import { useToast } from '@routeback/ui';
import { useEffect, useState } from 'react';
import { useApp } from '../../lib/app';
import { useMyShop } from '../../lib/shop';
import { enablePush, pushPermission, pushSupported, sendTestPush, type PushState } from '../../lib/push';

/** Me → Notifications: is this phone getting push, turn it on, send a test. */
export function NotificationsCard() {
  const app = useApp();
  const toast = useToast();
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { shop } = useMyShop();
  const check = () => {
    setErr(null);
    // A phone that can't answer within a few seconds is reported, never left blank.
    const timeout = new Promise<never>((_, no) => setTimeout(() => no(new Error('the phone didn\'t answer')), 5000));
    void Promise.race([pushPermission(), timeout]).then(setState).catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  };
  useEffect(check, []);
  if (app.mode !== 'cloud') return null;

  const turnOn = async () => {
    setBusy(true);
    const s = await enablePush().catch((e: unknown) => { setErr(e instanceof Error ? e.message : String(e)); return null; });
    setBusy(false);
    if (!s) return;
    setState(s);
    if (s === 'denied') toast('Notifications are blocked for RouteBack — turn them on in your phone\'s Settings → Apps → RouteBack → Notifications.');
  };
  const test = async () => {
    setBusy(true);
    const err = await sendTestPush(app.company.id);
    setBusy(false);
    toast(err ?? 'Test sent — it should pop up in a few seconds.');
  };

  let text: string;
  if (app.role === 'mechanic' && shop?.role === 'tech') text = `New-job notices go to ${shop.owner || 'your shop owner'}. Check the shop board for your jobs.`;
  else if (!pushSupported()) text = /iPhone|iPad/i.test(navigator.userAgent)
    ? 'iPhone notifications are coming soon. Until then, check Messages in the app.'
    : 'Notifications go to the RouteBack app on your phone. On this website, check Messages and your email.';
  else if (state === 'granted' && app.role === 'mechanic') text = 'On — new jobs, and jobs a company closes or takes back, come to this phone.';
  else if (state === 'granted') text = 'On — time off decisions, truck problem updates, uniforms, expiring documents and messages from your managers come to this phone.';
  else if (state === 'denied') text = 'Off. To turn them on: phone Settings → Apps → RouteBack → Notifications.';
  else if (app.role === 'mechanic') text = 'Get a notification when a company sends you a job, or closes or takes back one you have.';
  else text = 'Get a notification when your time off is decided, a truck problem you reported is fixed, uniforms are ready, a document is about to expire, or a manager sends a message.';

  return (
    <>
      <div className="section-title">Notifications</div>
      <div className="card">
        <p className="small" style={{ marginTop: 0 }}>{state === 'granted' && pushSupported() ? '🔔 ' : ''}{text}</p>
        {pushSupported() && err && (
          <>
            <p className="small" style={{ color: 'var(--danger)' }}>Couldn't check notifications on this phone: {err}</p>
            <button className="btn btn-block" onClick={check}>Try again</button>
          </>
        )}
        {pushSupported() && !err && state === null && <p className="muted small">Checking this phone…</p>}
        {pushSupported() && state === 'prompt' && <button className="btn btn-primary btn-block" disabled={busy} onClick={() => void turnOn()}>Turn on notifications</button>}
        {pushSupported() && state === 'denied' && <button className="btn btn-block" disabled={busy} onClick={() => void turnOn()}>Check again</button>}
        {pushSupported() && state === 'granted' && <button className="btn btn-block" disabled={busy} onClick={() => void test()}>Send me a test notification</button>}
      </div>
    </>
  );
}
